import type {ForwardedRef, ReactElement, SyntheticEvent} from 'react';
import React, {useMemo} from 'react';
import {getCircularSocialIconURLByType} from '@Libraries/social-media';
import {IconSize} from '@Components/icon-v2/icon.types';
import type {SocialAccount} from '@Components/social-media/account.vo';
import {getDefaultDisplayURL} from '@Components/social-media/account.vo';
import {ClickableDiv} from '@Components/clickable-div';
import {SocialPlatform} from '@Components/social-media/social-media.types';
import {PlatformMediaType} from '@Components/social-media/post.vo';
import styles from './social-profile-icon.module.scss';
import {Icon} from '@/components/icon-v2';

export enum SocialProfileIconSize {
  MEDIUM = 'MEDIUM',
  LARGE = 'LARGE',
}

interface SocialProfileIconProps {
  size: SocialProfileIconSize;
  account: SocialAccount;
  onClick?: VoidFunction;
  className?: string;
  isActive?: boolean;
  isDisabled?: boolean;
  showPlatformIcon?: boolean;
  showHoverOverlay?: boolean;
  isError?: boolean;
  platformMediaType?: PlatformMediaType;
}

function SocialProfileIcon(
  {
    account,
    onClick,
    size = SocialProfileIconSize.MEDIUM,
    className = '',
    isActive = false,
    isDisabled = false,
    showPlatformIcon = true,
    showHoverOverlay = true,
    isError = false,
    platformMediaType = PlatformMediaType.POST,
  }: SocialProfileIconProps,
  ref: ForwardedRef<HTMLDivElement>
): ReactElement {
  const getClassesForSize = (): string => {
    switch (size) {
      case SocialProfileIconSize.MEDIUM:
        return styles.medium;
      case SocialProfileIconSize.LARGE:
        return styles.large;
      default:
        return styles.medium;
    }
  };

  const getDisabledClass = (): string => {
    return `${isDisabled ? styles.disabled : ''}`;
  };

  const getPlatformSpecificImageClasses = (): string => {
    return account.type === SocialPlatform.YOUTUBE ? styles.shortHeight : '';
  };

  const getOverlayElements = (): ReactElement => {
    return (
      <>
        <Icon className={styles.iconClose} icon="icon-close" size={IconSize.SIZE_ICON_20} />
        <div className={styles.overlay} aria-hidden="true" />
      </>
    );
  };

  const onImageError = (event: SyntheticEvent<HTMLImageElement, Event>): void => {
    const {currentTarget} = event;
    currentTarget.onerror = null;
    currentTarget.src = getDefaultDisplayURL();
  };

  const socialPlatformIconElement = useMemo((): null | ReactElement => {
    if (showPlatformIcon) {
      return (
        <img
          className={`${styles.socialIcon} ${getPlatformSpecificImageClasses()} ${getDisabledClass()}`}
          src={getCircularSocialIconURLByType(account.type, platformMediaType)}
          alt=""
          aria-hidden="true"
        />
      );
    }

    return null;
  }, [showPlatformIcon, isDisabled]);

  const isClickable = onClick !== undefined;

  const displayImageElement = useMemo((): ReactElement => {
    return (
      <div className={`${styles.socialProfileContainer} ${isError ? styles.errorOutline : ''} ${getClassesForSize()}`}>
        <img
          className={`${styles.socialProfile} ${getDisabledClass()} flex-center`}
          src={account.displayURL ?? getDefaultDisplayURL()}
          alt={window.i18next.t('pmwjs_socialmedia_profile_picture', {socialMediaName: account.name})}
          onError={onImageError}
        />
      </div>
    );
  }, [isDisabled, isError]);

  const getActiveBorderClasses = (): string => {
    if (!isActive) {
      return ``;
    }

    if (isError) {
      return `${styles.activeErrorStyles}`;
    }

    return `${styles.activeStyles}`;
  };

  return (
    <ClickableDiv
      className={`${getClassesForSize()} ${className} ${styles.socialProfileIcon} ${isClickable ? styles.clickable : ''}  ${getActiveBorderClasses()}`}
      onClick={(): void => {
        if (onClick) {
          onClick();
        }
      }}
      ref={ref}
    >
      {displayImageElement}
      {socialPlatformIconElement}
      {isClickable && showHoverOverlay ? getOverlayElements() : null}
    </ClickableDiv>
  );
}

export default React.memo(React.forwardRef(SocialProfileIcon));
